import "@sendbird/uikit-react/dist/index.css";
import { PageHeaderWrapper, Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { User } from "@sendbird/chat";
import { FileMessage, UserMessage } from "@sendbird/chat/message";
import Channel from "@sendbird/uikit-react/Channel";
import { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import FileMessageItemBody from "@sendbird/uikit-react/ui/FileMessageItemBody";
import MessageContent from "@sendbird/uikit-react/ui/MessageContent";
import ThumbnailMessageItemBody from "@sendbird/uikit-react/ui/ThumbnailMessageItemBody";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { getExclusions } from "@src/app/api/exclusion";
import * as shiftApi from "@src/app/api/shift";
import { TabRouterPath } from "@src/app/routing/constant/tabRoute";
import { ChatPageWrapper } from "@src/appV2/Chat/ChatPageWrapper";
import { ShiftInviteRequestMessage } from "@src/appV2/Chat/ShiftInviteResponse/ShiftInviteRequestMessage";
import { ShiftTimeProposalMessage } from "@src/appV2/Chat/ShiftTimeProposalMessage/ShiftTimeProposalMessage";
import { CustomMessageTypes } from "@src/appV2/Chat/type";
import { getChannelShiftTime } from "@src/appV2/Chat/utils/getChannelShiftTime";
import { WorkerCredentialSharedMessage } from "@src/appV2/Chat/WorkerCredentialMessage/WorkerCredentialSharedMessage";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { useGetChartingNoncompliances } from "@src/appV2/redesign/Charting/api/useGetChartingNoncompliances";
import { useGetChatDetectedChartingCredentials } from "@src/appV2/redesign/Charting/api/useGetChatDetectedChartingCredentials";
import { ACTIVE_CHARTING_NONCOMPLIANCE_STATES } from "@src/appV2/redesign/Charting/constants";
import { DetectedChartingCredentialsCardContainer } from "@src/appV2/redesign/Charting/DetectedChartingCredentialsCardContainer";
import { useDetectCredentialKeywordInChat } from "@src/appV2/redesign/Charting/hooks/useDetectCredentialKeywordInChat";
import { UserCustomChatMessage } from "@src/appV2/redesign/Chat/UserCustomChatMessage";
import { WorkplaceChatHeader } from "@src/appV2/redesign/Chat/WorkplaceChatHeader";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { Shift } from "@src/lib/interface/src";
import { useAppSelector } from "@store/index";
import { format } from "date-fns";
import { ReactElement, useEffect, useReducer, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { getMessageInputDefinition } from "./channel";
import { fetchChannels } from "./chatProvider";
import { exclusionInitialState, exclusionReducer } from "./exclusionReducer";
import InputEtaMessage from "./InputEtaMessage";
import { SentHomeRequestMessage } from "./SentHomeRequestMessage";
import { SentHomeResponseMessage } from "./sentHomeResponseMessage";
import { getUpcomingShiftFromInputEtaMessage, hideMessage } from "./utils";
import { CHAT_MESSAGE_SPACING } from "../../appV2/Chat/constants";
import { BookedShiftMessage } from "../../appV2/redesign/Chat/BookedShiftMessage";
import { ChatSeparator } from "../../appV2/redesign/Chat/ChatSeparator";

const DETECT_CHARTING_CREDENTIALS_POLLING_INTERVAL_MS = 1000;

const getUserName = (user: User): string => user?.friendName || user?.nickname || user?.userId;
const getSenderName = (message: SendableMessageType): string =>
  message?.sender && getUserName(message?.sender);

export function ChatPage() {
  const worker = useDefinedWorker();
  const { facilityId } = useParams<{ facilityId: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const prefilledMessage = searchParams.get("prefilledMessage") ?? undefined;

  const channels = useAppSelector((store) => store.chatStore.channels);
  const currentChannelUrl = facilityId + "_" + worker.userId;
  const currentChannel = channels.find((channel) => channel.url === currentChannelUrl);

  const [exclusionState, exclusionDispatch] = useReducer(exclusionReducer, exclusionInitialState);

  const {
    data: chartingNoncompliances,
    isSuccess: isSuccessChartingNoncompliances,
    isLoading: isLoadingChartingNoncompliances,
  } = useGetChartingNoncompliances(
    { workerId: worker.userId, state: ACTIVE_CHARTING_NONCOMPLIANCE_STATES },
    { enabled: isDefined(worker.userId) }
  );

  const detectedChartingCredentialsWidgetEnabled = useCbhFlag(
    CbhFeatureFlag.ROLLOUT_DETECTED_CHARTING_CREDENTIALS,
    {
      defaultValue: false,
    }
  );

  const { hasRecentlyDetectedCredentialsKeyword } = useDetectCredentialKeywordInChat({
    workerId: worker.userId,
    workplaceId: facilityId,
    enabled: detectedChartingCredentialsWidgetEnabled,
  });

  const {
    data: chatDetectedChartingCredentials,
    isSuccess: isSuccessChatDetectedChartingCredentials,
    isLoading: isLoadingChatDetectedChartingCredentials,
  } = useGetChatDetectedChartingCredentials(
    { workerId: worker.userId, workplaceId: facilityId, status: "pending" },
    {
      enabled:
        isDefined(worker.userId) &&
        isDefined(facilityId) &&
        detectedChartingCredentialsWidgetEnabled,
      refetchInterval: (data) => {
        if (!hasRecentlyDetectedCredentialsKeyword) {
          return false;
        }

        const { length } = data?.data ?? [];
        if (length > 0) {
          // Stop polling if we already have detected credentials
          return false;
        }

        return DETECT_CHARTING_CREDENTIALS_POLLING_INTERVAL_MS;
      },
    }
  );

  const updateCredentialsModalState = useModalState();

  const hasPendingChatDetectedChartingCredentials =
    !isLoadingChatDetectedChartingCredentials &&
    isSuccessChatDetectedChartingCredentials &&
    chatDetectedChartingCredentials?.data.length > 0;

  const showDetectedChartingCredentialsWidget =
    detectedChartingCredentialsWidgetEnabled && hasPendingChatDetectedChartingCredentials;

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const dispatch = useDispatch();
  const ldFlags = useCbhFlags();

  const isLateForShiftEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_LATE_FOR_SHIFT, {
    defaultValue: false,
  });

  const urgentShiftsConfig = ldFlags[CbhFeatureFlag.URGENT_SHIFTS_CONFIG] ?? {};
  const [upcomingShifts, setUpcomingShifts] = useState<Shift[]>([]);

  useEffect(() => {
    fetchChannels({ sdk, dispatch });
  }, [dispatch, sdk]);

  useEffect(() => {
    async function fetchExclusion() {
      exclusionDispatch({ type: "exclusion/fetching" });
      try {
        const exclusions = await getExclusions({
          facilityId,
          agentId: worker.userId,
        });
        exclusionDispatch({
          type: "exclusion/success",
          // getExclusions returns list of exclusions
          // since we pass only one workerId, it is safe to assume that
          // if there is an exclusion, it is the exclusion between workplace and workerId
          data: { exclusion: exclusions[0] },
        });
      } catch (err: unknown) {
        exclusionDispatch({
          type: "exclusion/failed",
          data: {
            errorMessage: err instanceof Error ? err.message : String(err),
          },
        });
      }
    }

    fetchExclusion();
  }, [worker.userId, currentChannelUrl, facilityId]);

  useEffect(() => {
    if (!isLateForShiftEnabled) {
      return;
    }

    async function checkUpcomingShifts() {
      const nextTwoDaysShiftsList = await shiftApi.fetchNextTwoDaysShifts(worker.tmz ?? "");

      if (!nextTwoDaysShiftsList || (nextTwoDaysShiftsList ?? []).length === 0) {
        return;
      }

      setUpcomingShifts(nextTwoDaysShiftsList);

      await shiftApi.checkAttendanceBulk(environmentConfig.REACT_APP_URGENT_SHIFTS_SERVICE_URL, {
        shifts: nextTwoDaysShiftsList.map((shift: Shift) => {
          return {
            _id: shift._id,
            start: shift.start,
            facility: {
              userId: shift.facility?.userId,
            },
          };
        }),
      });
    }

    checkUpcomingShifts();
  }, [worker.tmz, isLateForShiftEnabled]);

  const { facilityName } = currentChannel?.metadata || {};

  const workerHasChartingNonComplianceAtWorkplace =
    !isLoadingChartingNoncompliances &&
    isSuccessChartingNoncompliances &&
    chartingNoncompliances?.data.length > 0 &&
    chartingNoncompliances.data.some(
      (noncompliance) => noncompliance.relationships.workplace.data.id === facilityId
    );

  const header = () => (
    <>
      <PageHeaderWrapper variant="tertiary" sx={{ boxShadow: (theme) => theme.shadows[2] }}>
        <WorkplaceChatHeader
          workplaceName={
            currentChannel?.shift?.facility?.name || facilityName || currentChannel?.name
          }
          channelShiftTime={getChannelShiftTime(currentChannel)}
          workplaceId={facilityId}
          fallbackGoBackPath={TabRouterPath.MY_SHIFTS}
        />
      </PageHeaderWrapper>
      {showDetectedChartingCredentialsWidget && (
        <DetectedChartingCredentialsCardContainer
          workplaceId={facilityId ?? ""}
          updateCredentialsModalState={updateCredentialsModalState}
        />
      )}
    </>
  );

  return (
    <ChatPageWrapper>
      <Channel
        isReactionEnabled={false}
        renderChannelHeader={header}
        channelUrl={currentChannelUrl}
        renderCustomSeparator={({ message }) => {
          if (hideMessage(message, upcomingShifts)) {
            return <></>;
          }
          const createdAt = new Date(message.createdAt);
          const date = format(createdAt, "MMM d, h:mm");
          const meridiem = format(createdAt, "a").toLowerCase();

          return (
            <ChatSeparator sx={{ marginY: CHAT_MESSAGE_SPACING }}>
              {date} {meridiem}
            </ChatSeparator>
          );
        }}
        renderMessageInput={getMessageInputDefinition({
          isLoading: exclusionState.isLoading && isLoadingChartingNoncompliances,
          actionBy: exclusionState.exclusion?.actionBy,
          sdk,
          workerHasChartingNonComplianceAtWorkplace,
          prefilledMessage,
        })}
        renderMessageContent={(props) => (
          <MessageContent
            {...props}
            renderMessageHeader={({ message }) => (
              <Text variant="caption" color="text.secondary" sx={{ paddingY: 2, paddingX: 0 }}>
                {getSenderName(message as SendableMessageType)}
              </Text>
            )}
            renderMessageBody={({ message, ...restProps }) => {
              if (message.messageType === "file") {
                const fileMessage = message as FileMessage;
                const isImage =
                  fileMessage.type?.startsWith("image/") || fileMessage.type?.startsWith("video/");

                if (isImage) {
                  return <ThumbnailMessageItemBody message={fileMessage} {...restProps} />;
                } else {
                  return <FileMessageItemBody message={fileMessage} {...restProps} />;
                }
              }

              return <UserCustomChatMessage message={message as UserMessage} {...restProps} />;
            }}
            renderSenderProfile={() => null}
          />
        )}
        renderMessage={({ message }) => {
          if (hideMessage(message, upcomingShifts)) {
            return <></>;
          }

          if (message.customType === CustomMessageTypes.FCM_REQUEST) {
            const metaData = currentChannel?.cachedMetaData as {
              facilityName: string;
            };
            return <SentHomeRequestMessage facilityName={metaData?.facilityName} />;
          }
          if (
            message.customType === CustomMessageTypes.FCM_APPROVAL ||
            message.customType === CustomMessageTypes.FCM_REJECT
          ) {
            return <SentHomeResponseMessage message={message} />;
          }

          if (message.customType === CustomMessageTypes.SHIFT_INVITE_REQUEST) {
            return <ShiftInviteRequestMessage message={message} />;
          }

          if (message.customType === CustomMessageTypes.SHIFT_TIME_PROPOSAL_CREATED) {
            return <ShiftTimeProposalMessage message={message} />;
          }

          if (message.customType === CustomMessageTypes.WORKER_CREDENTIAL_SHARED) {
            return (
              <WorkerCredentialSharedMessage
                message={message}
                workplaceId={facilityId}
                workerId={worker.userId}
              />
            );
          }

          if (message.customType === CustomMessageTypes.INPUT_ETA) {
            const shift = getUpcomingShiftFromInputEtaMessage(message, upcomingShifts);

            return isDefined(shift) ? (
              <InputEtaMessage shift={shift} urgentShiftsConfig={urgentShiftsConfig} />
            ) : (
              <></>
            );
          }

          if (message.customType === CustomMessageTypes.SHIFT_BOOKED) {
            return <BookedShiftMessage message={message} />;
          }

          if (!message.customType && message.data) {
            try {
              const data = JSON.parse(message.data);

              const { sender } = message as SendableMessageType;
              if (sender && !sender.friendName) {
                const chatTitle = `${data.workplaceUserName.split(" ")[0]} from ${sender.nickname}`;
                sender.nickname = chatTitle;
                sender.friendName = chatTitle;
              }
            } catch (error) {
              console.error(error);
            }
          }

          // We have to return undefined to render the sendbird message component and using any other value will make the message not visible on the UI.
          return null as unknown as ReactElement;
        }}
      />
    </ChatPageWrapper>
  );
}
